import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ListChecks, Scissors, Clock, Award, CheckCircle2, PlayCircle, Repeat, MousePointerClick,
  Instagram, Coins, Users, TrendingUp, Star,
} from "lucide-react";
import { resolveImg } from "@/lib/api";
import { gaEvent } from "@/lib/analytics";
import { ContactInstructor } from "@/components/site/ContactInstructor";

const INSTRUCTOR = "https://images.unsplash.com/photo-1717700921740-a1440f3b89a4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function Eyebrow({ children }) {
  return (
    <div className="mb-5 flex items-center gap-3">
      <span className="h-px w-8 bg-[#DCA846]" />
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#DCA846]">{children}</span>
    </div>
  );
}

function Section({ children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`mx-auto max-w-7xl px-6 py-20 md:py-28 ${className}`}>{children}</section>
  );
}

/* 1 — WHY EPIC FADE ACADEMY */
export function WhyEpicFade({ c = {} } = {}) {
  const benefits = [
    { icon: ListChecks, title: "Step-by-Step Training", text: "Clear techniques explained from start to finish." },
    { icon: Scissors, title: "Real Barber Techniques", text: "Practical methods designed for real clients and real barbershop situations." },
    { icon: Clock, title: "Learn at Your Own Pace", text: "Pause, replay and practice each technique whenever you need." },
    { icon: Award, title: "Professional Results", text: "The small details that create cleaner blends, sharper finishes and consistent haircuts." },
  ];
  return (
    <Section testid="section-why" className="border-t border-white/5">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={reveal}>
        <Eyebrow>{c.eyebrow ?? "Why Epic Fade Academy"}</Eyebrow>
        <h2 className="max-w-3xl font-display text-3xl font-bold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl md:text-5xl">
          {c.title ?? "Master the details that separate a good fade from a great one."}
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-zinc-400">
          {c.body ?? "Professional barbering is more than following steps. It is understanding shape, weight, guidelines, blending, texture and finishing. Epic Fade Academy breaks professional techniques into clear, practical lessons you can watch, practice and master at your own pace."}
        </p>
      </motion.div>

      <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
        {benefits.map((b, i) => (
          <motion.div key={b.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
            className="group">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#DCA846]/30 text-[#DCA846] transition-colors group-hover:bg-[#DCA846] group-hover:text-black">
              <b.icon className="h-5 w-5" />
            </div>
            <h3 className="mt-5 font-display text-lg uppercase tracking-wide text-white">{b.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-400">{b.text}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* 2 — MEET YOUR INSTRUCTOR */
export function MeetInstructor({ c = {} } = {}) {
  const inst = c || {};
  const img = inst.image || INSTRUCTOR;
  const highlights = inst.highlights?.length ? inst.highlights : ["15+ Years Experience", "Licensed Barber in Canada", "Real Client Techniques", "Beginner to Advanced Training"];
  return (
    <Section testid="section-instructor" className="border-t border-white/5">
      <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="relative order-1">
          <div className="grain relative overflow-hidden rounded-2xl border border-white/10 gold-glow">
            <img src={img.startsWith("http") ? img : resolveImg(img)} alt="Instructor" loading="lazy" className="aspect-[4/5] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-5 -right-4 rounded-xl border border-[#DCA846]/30 bg-black/80 px-5 py-3 backdrop-blur">
            <p className="font-display text-3xl font-bold text-[#DCA846] leading-none">{inst.experience_years || "15+"}</p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400">Years Experience</p>
          </div>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={reveal} className="order-2">
          <Eyebrow>{inst.eyebrow || "Meet Your Instructor"}</Eyebrow>
          <h2 className="font-display text-3xl font-bold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl">
            {inst.title || "Learn from real barbershop experience"}
          </h2>
          <p className="mt-6 text-base leading-relaxed text-zinc-400">
            {inst.bio1 || "Learn professional barbering techniques developed through more than 15 years of hands-on experience behind the chair."}
          </p>
          {(inst.bio2 ?? "From building clean guidelines to controlling weight, creating smooth transitions and perfecting the final details, every lesson focuses on techniques you can actually use with your clients.") ? (
            <p className="mt-4 text-base leading-relaxed text-zinc-400">
              {inst.bio2 ?? "From building clean guidelines to controlling weight, creating smooth transitions and perfecting the final details, every lesson focuses on techniques you can actually use with your clients."}
            </p>
          ) : null}
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {highlights.map((h) => (
              <div key={h} className="flex items-center gap-3 border-l-2 border-[#DCA846]/40 pl-3">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-[#DCA846]" />
                <span className="text-sm text-zinc-200">{h}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {inst.instagram ? (
              <a href={inst.instagram} target="_blank" rel="noreferrer" data-testid="instructor-instagram"
                onClick={() => gaEvent("instagram_click", { link_url: inst.instagram, source: "instructor_section" })}
                className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-5 py-2.5 font-display text-sm uppercase tracking-wide text-white transition-colors hover:border-[#DCA846]/50 hover:bg-white/5">
                <Instagram className="h-4 w-4" /> Instagram
              </a>
            ) : null}
            {inst.show_email !== false ? <ContactInstructor variant="buttons" context="the instructor" /> : null}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

/* 3 — WHAT YOU'LL LEARN */
export function WhatYoullLearn({ c = {} } = {}) {
  const skills = [
    "Fade Guidelines", "Smooth Blending", "Clipper Control", "Skin Fades", "Low, Mid & High Fades",
    "Scissor Over Comb", "Texture & Weight Control", "Line Ups", "Beard Blending", "Finishing Techniques",
  ];
  return (
    <Section testid="section-skills" className="border-t border-white/5">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={reveal}>
        <Eyebrow>What You'll Learn</Eyebrow>
        <h2 className="max-w-3xl font-display text-3xl font-bold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl md:text-5xl">
          Build the skills behind a professional fade
        </h2>
      </motion.div>
      <div className="mt-12 grid grid-cols-1 gap-x-12 sm:grid-cols-2 lg:grid-cols-3">
        {skills.map((s, i) => (
          <motion.div key={s} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
            className="group flex items-baseline gap-4 border-b border-white/10 py-5 transition-colors hover:border-[#DCA846]/50">
            <span className="font-display text-sm text-[#DCA846]/70 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
            <span className="font-display text-xl uppercase tracking-wide text-zinc-200 transition-all group-hover:translate-x-1 group-hover:text-[#DCA846]">{s}</span>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* 4 — HOW THE TRAINING WORKS */
export function HowItWorks({ c = {} } = {}) {
  const steps = [
    { n: "01", icon: MousePointerClick, title: "Choose Your Course", text: "Select the technique or haircut you want to master." },
    { n: "02", icon: PlayCircle, title: "Watch Step-by-Step", text: "Follow professional demonstrations with clear explanations." },
    { n: "03", icon: Repeat, title: "Practice & Improve", text: "Replay lessons whenever needed and develop your technique at your own pace." },
  ];
  return (
    <Section testid="section-how" className="border-t border-white/5">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={reveal} className="text-center">
        <div className="mb-5 flex items-center justify-center gap-3">
          <span className="h-px w-8 bg-[#DCA846]" />
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#DCA846]">{c.eyebrow ?? "How The Training Works"}</span>
          <span className="h-px w-8 bg-[#DCA846]" />
        </div>
        <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl md:text-5xl">{c.title ?? "Watch. Practice. Master."}</h2>
      </motion.div>

      <div className="mt-16 grid gap-10 md:grid-cols-3">
        {steps.map((s, i) => (
          <motion.div key={s.n} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
            className="relative pt-4">
            <span className="pointer-events-none absolute -top-6 left-0 font-display text-7xl font-bold text-white/5 select-none">{s.n}</span>
            <div className="relative">
              <s.icon className="h-7 w-7 text-[#DCA846]" />
              <h3 className="mt-4 font-display text-xl uppercase tracking-wide text-white">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">{s.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* HOMEPAGE COMMUNITY PROMO — CREDIT-BASED IG SUPPORT */
export function CommunityPromo({ c = {} } = {}) {
  const steps = [
    { icon: Instagram, n: "01", title: "Submit Your Work", text: "Drop the link to your Instagram post or reel — no re-uploading required." },
    { icon: Users, n: "02", title: "Support Other Barbers", text: "Open a barber's post on Instagram, engage, and confirm to earn +3 credits every time." },
    { icon: TrendingUp, n: "03", title: "Get More Exposure", text: "Spend your credits to push your own work into the community support queue and grow your reach." },
  ];
  return (
    <Section testid="section-community" className="border-t border-white/5">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} variants={reveal}
        className="grain relative overflow-hidden rounded-2xl border border-[#DCA846]/25 bg-gradient-to-br from-[#DCA846]/12 via-black/40 to-transparent p-8 md:p-14">
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[#DCA846]/10 blur-3xl" />
        <div className="relative grid items-start gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
          <div>
            <Eyebrow>Epic Fade Community</Eyebrow>
            <h2 className="max-w-2xl font-display text-3xl font-bold uppercase leading-[1.02] tracking-tight text-white sm:text-4xl md:text-5xl">
              Support barbers. <span className="text-[#DCA846]">Earn credits.</span> Get your work seen.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300">
              {c.body ?? "A real support network built into your membership. Back other barbers on Instagram to earn credits, then spend those credits to put your own posts in front of the whole community — a fair, give-to-get exposure engine."}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2.5 rounded-xl border border-[#DCA846]/30 bg-[#DCA846]/10 px-4 py-2.5">
                <Coins className="h-5 w-5 text-[#DCA846]" />
                <span className="font-display text-lg text-[#DCA846]">+3</span>
                <span className="text-xs uppercase tracking-wide text-zinc-400">credits / support</span>
              </div>
              <span className="text-sm text-zinc-400">Included free with your Epic Fade account.</span>
            </div>
            <Link to="/community" onClick={() => gaEvent("community_click", { source: "home_community_promo" })}>
              <button data-testid="join-community-btn" className="mt-8 rounded-sm bg-[#DCA846] px-8 py-3.5 font-display uppercase tracking-wide text-black transition-colors hover:bg-[#C5963B] gold-glow">
                {c.button_text ?? "Join The Community"}
              </button>
            </Link>
          </div>

          <div className="space-y-4">
            {steps.map((s, i) => (
              <motion.div key={s.n} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
                className="group flex gap-4 rounded-xl border border-white/10 bg-black/40 p-5 transition-colors hover:border-[#DCA846]/40">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#DCA846]/30 text-[#DCA846] transition-colors group-hover:bg-[#DCA846] group-hover:text-black">
                  <s.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-xs text-[#DCA846]/70 tabular-nums">{s.n}</span>
                    <h3 className="font-display text-base uppercase tracking-wide text-white">{s.title}</h3>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-zinc-400">{s.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </Section>
  );
}
export function StudentResults({ items = [], c = {} } = {}) {
  if (!items.length) return null;
  return (
    <Section testid="section-results" className="border-t border-white/5">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={reveal}>
        <Eyebrow>{c.eyebrow ?? "Student Results"}</Eyebrow>
        <h2 className="max-w-3xl font-display text-3xl font-bold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl md:text-5xl">
          {c.title ?? "Real fades from Epic Fade students"}
        </h2>
        {(c.subtitle ?? "See the results students are getting with the training.") ? (
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-zinc-400">{c.subtitle ?? "See the results students are getting with the training."}</p>
        ) : null}
      </motion.div>
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((r, i) => (
          <motion.div key={r.id} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}
            className={`group overflow-hidden rounded-xl border ${r.featured ? "border-[#DCA846]/50" : "border-white/10"} bg-zinc-900/40`}>
            {r.image ? (
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={resolveImg(r.image)} alt={r.name || "Student result"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {r.featured ? <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-[#DCA846] px-2.5 py-1 text-[10px] font-bold uppercase text-black"><Star className="h-3 w-3 fill-black" /> Featured</span> : null}
              </div>
            ) : null}
            <div className="p-5">
              <div className="flex items-center justify-between">
                <p className="font-display uppercase tracking-wide text-white">{r.name}</p>
                {r.instagram ? (
                  <a href={r.instagram.startsWith("http") ? r.instagram : `https://instagram.com/${r.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" onClick={() => gaEvent("instagram_click", { link_url: r.instagram, source: "student_results" })} className="text-[#DCA846] hover:text-[#C5963B]"><Instagram className="h-4 w-4" /></a>
                ) : null}
              </div>
              {r.testimonial ? <p className="mt-2 text-sm leading-relaxed text-zinc-400">{r.testimonial}</p> : null}
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

export function MotivationalBanner({ c = {} } = {}) {
  return (
    <Section testid="section-motivation" className="border-t border-white/5 text-center">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={reveal} className="mx-auto max-w-3xl">
        <h2 className="font-display text-3xl font-bold uppercase leading-[1.1] tracking-tight text-white sm:text-4xl">
          Better technique. <span className="text-[#DCA846]">Cleaner results.</span> More confidence.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-zinc-400">
          {c.body ?? "Great barbering comes from understanding the details. Learn the techniques, understand why they work, and build the confidence to create consistent results behind the chair."}
        </p>
      </motion.div>
    </Section>
  );
}
